import React from "react";
import { motion } from "framer-motion";

const OverviewChallenge = () => {
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.2 },
        },
    };

    const fadeUpVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
    };

    const imageVariants = {
        hidden: { opacity: 0, scale: 0.95 },
        visible: { opacity: 1, scale: 1, transition: { duration: 0.9, ease: "easeOut" } },
    };

    return (
        <section className="w-full bg-white py-12 sm:py-16 md:py-20 lg:py-24 text-left relative z-10 font-sans overflow-hidden">
            <div className="max-w-[1440px] mx-auto w-full px-5 sm:px-10 lg:px-[80px] flex flex-col gap-14 sm:gap-20 md:gap-24 lg:gap-28">

                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    className="flex lg:flex-row flex-col gap-4 lg:gap-8 items-start w-full"
                >
                    <motion.div variants={fadeUpVariants} className="lg:col-span-4 w-full">
                        <h2 className="text-[20px] md:text-[30px] font-[600] font-sora text-[#01386A] leading-tight tracking-tight">
                            Overview
                        </h2>
                    </motion.div>
                    <motion.div variants={fadeUpVariants} className="lg:max-w-[529px] w-full lg:pt-2">
                        <p className="text-[14px] md:text-[16px] text-[#49577A] font-graphik leading-relaxed font-[500] break-words">
                            A regional community bank in the US Midwest serving local businesses and agricultural customers was experiencing slow loan approvals, rising IT costs, and lower employee as well as customer satisfaction. We worked closely with the bank to review its lending processes and supporting technology.
                        </p>
                    </motion.div>
                </motion.div>

                <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-center justify-between w-full">

                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-100px" }}
                        className="lg:max-w-[558px] w-full flex flex-col min-w-0"
                    >
                        <motion.h2 variants={fadeUpVariants} className="text-[20px] md:text-[30px] font-[600] font-sora text-[#01386A] leading-tight tracking-tight mb-5 sm:mb-6">
                            Industry Challenge
                        </motion.h2>

                        <motion.p variants={fadeUpVariants} className="text-[14px] sm:text-[16px] text-[#49577A] mb-5 sm:mb-6 font-[500] font-graphik">
                            Despite prior digital investments, the bank continued to face:
                        </motion.p>

                        <motion.ul variants={fadeUpVariants} className="space-y-3.5 sm:space-y-4 w-full pl-1">
                            {[
                                "Agriculture loan origination turnaround times average 49 days",
                                "High manual intervention across agriculture and commercial lending",
                                "Disconnected systems across the lending value chain, causing data duplication and delays",
                                "IT costs are disproportionate to the business value delivered",
                                "Declining customer satisfaction and limited operational scalability"
                            ].map((challenge, index) => (
                                <li key={index} className="flex items-center gap-3 w-full text-[14px] sm:text-[16px] text-[#49577A] font-[500] font-graphik break-words">
                                    <span className="text-[#0F3661] text-[16px] leading-none select-none pt-0.5">•</span>
                                    <span className="flex-1">{challenge}</span>
                                </li>
                            ))}
                        </motion.ul>
                    </motion.div>

                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-100px" }}
                        variants={imageVariants}
                        className="lg:max-w-[529px] w-full shrink-0 flex justify-center lg:justify-end"
                    >
                        <div className="w-full max-w-[500px] lg:max-w-none aspect-[4/3] sm:aspect-[3/2] lg:aspect-[4/3] overflow-hidden shadow-sm relative bg-slate-900">
                            <motion.img
                                whileHover={{ scale: 1.03 }}
                                transition={{ duration: 0.6 }}
                                src="/assets/insights/img7.png"
                                alt="Industry Challenge Abstract Sphere Graphics"
                                className="w-full h-full object-cover absolute inset-0"
                            />
                        </div>
                    </motion.div>

                </div>

            </div>
        </section>
    );
};

export default OverviewChallenge;